import { useCallback, useEffect, useMemo } from 'react';
import { useStore } from 'zustand/react';
import { useShallow } from 'zustand/react/shallow';
import { makeSelectStepState, selectEditable, selectPeople } from '@stores/stepAccess/StepAccess.selectors';
import { peopleForStep } from './helper';
import StepAccess from './index';

const SEARCH_DEBOUNCE_MS = 300;

// One step's access, connected to the store. The picker itself stays controlled and unaware of the
// save, which is why this file -- not that one -- owns the debounce and the write.
const StepAccessSection = ({ store, stepId, savedStepIds = new Set() }) => {
    const selectStepState = useMemo(() => makeSelectStepState(stepId), [stepId]);
    const { pending, query, directory, saving } = useStore(store, useShallow(selectStepState));
    const people = useStore(store, selectPeople);
    const editable = useStore(store, selectEditable);
    const toggle = useStore(store, (state) => state.toggle);
    const setQuery = useStore(store, (state) => state.setQuery);
    const search = useStore(store, (state) => state.search);
    const commit = useStore(store, (state) => state.commit);

    useEffect(() => {
        if (!query.trim()) {
            return undefined;
        }
        const controller = new AbortController();
        const timer = setTimeout(() => search(stepId, controller.signal), SEARCH_DEBOUNCE_MS);
        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [query, stepId, search]);

    // What was saved is announced by the response itself, so nothing here composes a sentence about
    // it: which of the four outcomes happened is only knowable from the state before the write.
    const handleCommit = useCallback(() => commit(stepId), [commit, stepId]);

    const stepPeople = useMemo(() => peopleForStep(people, stepId), [people, stepId]);

    return (
        <StepAccess
            stepId={stepId}
            people={stepPeople}
            directory={directory}
            pending={pending}
            query={query}
            onQueryChange={(value) => setQuery(stepId, value)}
            onToggle={(person) => toggle(stepId, person)}
            onCommit={handleCommit}
            editable={editable && !saving}
            unsavedStep={!savedStepIds.has(String(stepId))}
        />
    );
};

export default StepAccessSection;
